'use client';

import React, { useEffect, useMemo, useState } from 'react';

import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import useSWR from 'swr';

import { ThreadFeedItem } from '@/components/FeedItem';
import { useUserInfo } from '@/context/UserInfoContext';
import { useFeedSummaryPolling } from '@/hooks/useFeedSummaryPolling';
import { FeedItem, urlFetcher } from '@/lib/api';
import { API_URL } from '@/lib/consts';

import {
  buildCanonicalHomeFeedUrl,
  buildHomeFeedUrl,
  HOME_FEED_INCREMENT,
  MAX_HOME_FEED_COUNT,
  normalizeHomeFeedCount,
} from './homeFeedQuery';

const HomeFeed: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const rawSearch = searchParams.toString();
  const { userInfo } = useUserInfo();
  const numItems = useMemo(
    () => normalizeHomeFeedCount(searchParams.get('count')),
    [rawSearch, searchParams]
  );
  const currentUrl = rawSearch ? `${pathname}?${rawSearch}` : pathname;
  const canonicalUrl = useMemo(
    () => buildCanonicalHomeFeedUrl(pathname, rawSearch),
    [pathname, rawSearch]
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreSnapshot, setLoadMoreSnapshot] = useState<FeedItem[] | null>(null);
  const [pendingLoadMoreTarget, setPendingLoadMoreTarget] = useState<string | null>(null);

  useEffect(() => {
    if (canonicalUrl !== currentUrl) {
      router.replace(canonicalUrl, { scroll: false });
    }
  }, [canonicalUrl, currentUrl, router]);

  // Use the public feed endpoint for non-logged-in users, otherwise use the personalized feed
  const endpoint = userInfo ? 'unified' : 'public';
  const feedEndpoint = `${endpoint}/${numItems}`;
  const feedRequestTarget = `${API_URL}/feed/${feedEndpoint}`;

  // This will fetch all feed items up to the specified number
  const { data: currentFeedItems, error } = useSWR<FeedItem[]>(feedRequestTarget, urlFetcher, {
    // Don't revalidate on focus to prevent unexpected refreshes
    revalidateOnFocus: false,
  });

  const hasResolvedCurrentRequest = currentFeedItems !== undefined || !!error;
  const visibleFeedItems =
    currentFeedItems !== undefined ? currentFeedItems : isLoadingMore ? loadMoreSnapshot : null;

  useEffect(() => {
    if (
      !isLoadingMore ||
      !pendingLoadMoreTarget ||
      pendingLoadMoreTarget !== feedRequestTarget ||
      !hasResolvedCurrentRequest
    ) {
      return;
    }

    setIsLoadingMore(false);
    setLoadMoreSnapshot(null);
    setPendingLoadMoreTarget(null);
  }, [feedRequestTarget, hasResolvedCurrentRequest, isLoadingMore, pendingLoadMoreTarget]);

  // Function to load more items
  const handleLoadMoreUnifiedFeed = () => {
    if (isLoadingMore) return;

    const next = Math.min(numItems + HOME_FEED_INCREMENT, MAX_HOME_FEED_COUNT);
    const nextFeedRequestTarget = `${API_URL}/feed/${endpoint}/${next}`;

    setLoadMoreSnapshot(currentFeedItems ?? []);
    setPendingLoadMoreTarget(nextFeedRequestTarget);
    setIsLoadingMore(true);
    router.replace(buildHomeFeedUrl(pathname, rawSearch, next), { scroll: false });
  };

  // Enable summary polling for feed items without summaries
  useFeedSummaryPolling({
    feedItems: currentFeedItems ?? [],
    feedRequestTarget,
    enabled: !error && !!currentFeedItems && currentFeedItems.length > 0,
  });

  return (
    <Box component="section" sx={{ width: '100%', mb: 3 }} aria-labelledby="unified-feed">
      <h2 id="unified-feed">{userInfo ? 'Your Feed' : 'Product Reviews and Discussion'}</h2>
      {visibleFeedItems && visibleFeedItems.length > 0 ? (
        <Stack spacing={2}>
          {visibleFeedItems.map((item, index) => (
            <ThreadFeedItem
              key={index}
              item={item}
              showTopic={true}
              currentUser={userInfo || undefined}
            />
          ))}
          {numItems < MAX_HOME_FEED_COUNT && (
            <Box sx={{ textAlign: 'center' }}>
              <Button
                onClick={handleLoadMoreUnifiedFeed}
                aria-label="Load more content"
                disabled={isLoadingMore}
                variant="outlined"
                size="medium"
              >
                {isLoadingMore ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </Stack>
      ) : !hasResolvedCurrentRequest ? (
        <Stack spacing={2}>
          {[...Array(5)].map((_, index) => (
            <Box key={index}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mb: 1 }} />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          ))}
        </Stack>
      ) : error ? (
        <p>Error loading content. Please try again later.</p>
      ) : (
        <p>
          Nothing to see! {!userInfo && <Link href="/login">Log in</Link>} to see personalized
          content.
        </p>
      )}
    </Box>
  );
};

export default HomeFeed;
